// @ts-strict-ignore
import { useForm } from 'react-hook-form';
import Router from 'next/router';
import styled from 'styled-components';
import { captureException } from '@sentry/nextjs';
import { useDispatch } from 'src/store/store';
import { setAlert } from 'src/components/alert/actions';
import i18n from 'src/utils/translate';
import { passwordMinLength, passwordMaxLength, passwordErrors } from 'src/constants/validationErrors';
import { DefaultLayout } from 'src/components/layouts';
import { PrimaryButton } from 'styles-js/buttons';
import { Input } from 'styles-js/form';
import { Container, Title, Label, StyledError } from 'src/modules/resetPassword/sharedStyles';
import { postPublic } from 'src/utils/api';
export default function RequestResetPage({
  code
}: {
  code: string;
}): JSX.Element {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    watch
  } = useForm();
  const watchPassword = watch('password', '');
  const onSubmit = async ({
    password,
    confirmPassword
  }: {
    password: string;
    confirmPassword: string;
  }) => {
    try {
      const response = await postPublic(`password-reset/${code}`, {
        password,
        confirmPassword
      });
      if (response) {
        dispatch(setAlert(i18n.t('Your password has been changed! Please log in with your new password.'), 'success', 'none'));
        Router.push('/login', '/login', {
          shallow: true
        }); // shallow to skip getInitialProps
      }
    } catch (ex) {
      dispatch(setAlert());
      captureException(ex);
    }
  };
  return <DefaultLayout data-sentry-element="DefaultLayout" data-sentry-component="RequestResetPage" data-sentry-source-file="resetPasswordPage.tsx">
      <Container onSubmit={handleSubmit(onSubmit)} data-sentry-element="Container" data-sentry-source-file="resetPasswordPage.tsx">
        <Title data-sentry-element="Title" data-sentry-source-file="resetPasswordPage.tsx">{i18n.t('Create a new password')}</Title>
        <Text data-sentry-element="Text" data-sentry-source-file="resetPasswordPage.tsx">{i18n.t('To make sure your account is secure, this will log you out of all devices.')}</Text>

        <InputWrapper data-sentry-element="InputWrapper" data-sentry-source-file="resetPasswordPage.tsx">
          <Label htmlFor="password" data-sentry-element="Label" data-sentry-source-file="resetPasswordPage.tsx">{i18n.t('New password')}</Label>
          <Input $isInvalid={!!errors.password} id="password" type="password" {...register('password', {
          minLength: {
            value: passwordMinLength,
            message: passwordErrors.min
          },
          maxLength: {
            value: passwordMaxLength,
            message: passwordErrors.max
          },
          required: true
        })} data-sentry-element="Input" data-sentry-source-file="resetPasswordPage.tsx" />
          {errors.password?.message && <StyledError>{(errors.password?.message as string)}</StyledError>}
        </InputWrapper>
        <InputWrapper data-sentry-element="InputWrapper" data-sentry-source-file="resetPasswordPage.tsx">
          <Label htmlFor="confirmPassword" data-sentry-element="Label" data-sentry-source-file="resetPasswordPage.tsx">{i18n.t('Confirm new password')}</Label>
          <Input $isInvalid={!!errors.confirmPassword} id="confirmPassword" type="password" {...register('confirmPassword', {
          required: {
            value: true,
            message: i18n.t('Type your new password again')
          },
          validate: value => value === watchPassword
        })} data-sentry-element="Input" data-sentry-source-file="resetPasswordPage.tsx" />
          {errors.confirmPassword?.message && <StyledError>{(errors.confirmPassword?.message as string)}</StyledError>}
          {errors.confirmPassword?.type === 'validate' && <StyledError>{passwordErrors.match}</StyledError>}
        </InputWrapper>
        <PrimaryButton type="submit" data-sentry-element="PrimaryButton" data-sentry-source-file="resetPasswordPage.tsx">{i18n.t('Reset password')}</PrimaryButton>
      </Container>
    </DefaultLayout>;
}
const Text = styled.p`
  margin-bottom: 20px;
  color: ${({
  theme
}) => theme.colorGreyDark};
`;
const InputWrapper = styled.div`
  margin: 15px 0 2px;
`;